import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";
import { Check, X, DollarSign, CreditCard, Banknote, Printer, Download } from "lucide-react";
import { exportToCSV, handlePrint } from "../../utils/exportUtils";

const CheckInPage = () => {
  const [playdays, setPlaydays] = useState([]);
  const [selectedPlayday, setSelectedPlayday] = useState("");
  const [entries, setEntries] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlaydays();
  }, []);

  useEffect(() => {
    if (selectedPlayday) {
      fetchEntries();
    }
  }, [selectedPlayday]);

  const fetchPlaydays = async () => {
    try {
      const [playdaysRes, eventsRes] = await Promise.all([
        axios.get(`${API}/playdays`),
        axios.get(`${API}/events`)
      ]);
      setPlaydays(playdaysRes.data);
      setEvents(eventsRes.data);
      if (playdaysRes.data.length > 0) {
        setSelectedPlayday(playdaysRes.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/entries?playday_id=${selectedPlayday}`);
      setEntries(response.data);
    } catch (error) {
      toast.error("Failed to fetch entries");
    }
  };

  const handleCheckIn = async (entryId, paymentType) => {
    try {
      await axios.put(`${API}/entries/${entryId}/checkin`, {
        is_paid: true,
        payment_type: paymentType
      });
      toast.success("Checked in successfully");
      fetchEntries();
    } catch (error) {
      toast.error("Check-in failed");
    }
  };

  const handleUncheckIn = async (entryId) => {
    try {
      await axios.put(`${API}/entries/${entryId}/checkin`, {
        is_paid: false,
        payment_type: null
      });
      toast.success("Check-in reversed");
      fetchEntries();
    } catch (error) {
      toast.error("Failed to reverse check-in");
    }
  };

  const getEventName = (eventId) => {
    const event = events.find(e => e.id === eventId);
    return event ? event.name : eventId;
  };

  const getSelectedPlaydayName = () => {
    const playday = playdays.find(p => p.id === selectedPlayday);
    return playday ? `${playday.name} (${playday.date})` : "";
  };

  const totalFeesDue = entries.filter(e => !e.is_paid).reduce((sum, e) => sum + e.total_fees, 0);
  const totalFeesCollected = entries.filter(e => e.is_paid).reduce((sum, e) => sum + e.total_fees, 0);

  const handleExportCheckIn = () => {
    const exportData = entries.map(entry => ({
      contestant: entry.contestant_name,
      events: entry.event_ids.map(eid => getEventName(eid)).join("; "),
      total_fees: entry.total_fees.toFixed(2),
      status: entry.is_paid ? "PAID" : "UNPAID",
      payment_type: entry.payment_type || ""
    }));
    
    exportToCSV(
      exportData, 
      `checkin_${getSelectedPlaydayName().replace(/[^a-z0-9]/gi, '_')}.csv`,
      ["contestant", "events", "total_fees", "status", "payment_type"]
    );
    toast.success("Check-in list exported to CSV");
  };

  const handlePrintCheckIn = () => {
    handlePrint("Check-In List", getSelectedPlaydayName());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 print-container" data-testid="checkin-page">
      <div className="flex items-center justify-between no-print">
        <div>
          <h1 className="font-heading font-bold text-3xl text-text-primary">Check-In</h1>
          <p className="text-text-secondary mt-1">Mark contestants as paid</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrintCheckIn} disabled={entries.length === 0} data-testid="print-checkin-btn">
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button variant="outline" onClick={handleExportCheckIn} disabled={entries.length === 0} data-testid="export-checkin-btn">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block print-header">
        <h1 className="font-heading font-bold text-2xl">TTP Play Day - Check-In List</h1>
        <p>{getSelectedPlaydayName()}</p>
        <p className="mt-2">Fees Due: ${totalFeesDue.toFixed(2)} | Collected: ${totalFeesCollected.toFixed(2)}</p>
      </div>

      {/* Playday Selector */}
      <div className="flex gap-4 items-end no-print">
        <div className="w-64">
          <label className="text-sm font-medium text-text-primary mb-2 block">Select Play Day</label>
          <Select value={selectedPlayday} onValueChange={setSelectedPlayday}>
            <SelectTrigger data-testid="playday-select">
              <SelectValue placeholder="Select a play day" />
            </SelectTrigger>
            <SelectContent>
              {playdays.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} ({p.date})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 no-print">
        <Card className="border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-secondary text-primary">
                <DollarSign className="h-6 w-6" />
              </div>
              <div>
                <p className="text-text-secondary text-sm">Total Entries</p>
                <p className="font-heading font-bold text-2xl text-text-primary">{entries.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-status-warning/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-status-warning/20 text-status-warning">
                <X className="h-6 w-6" />
              </div>
              <div>
                <p className="text-text-secondary text-sm">Fees Due</p>
                <p className="font-heading font-bold text-2xl text-status-warning">${totalFeesDue.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-status-success/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-status-success/20 text-status-success">
                <Check className="h-6 w-6" />
              </div>
              <div>
                <p className="text-text-secondary text-sm">Collected</p>
                <p className="font-heading font-bold text-2xl text-status-success">${totalFeesCollected.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Entries Table */}
      <div className="bg-white rounded-xl border border-primary/20 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50">
              <TableHead className="font-heading font-bold text-text-primary">Contestant</TableHead>
              <TableHead className="font-heading font-bold text-text-primary">Events</TableHead>
              <TableHead className="font-heading font-bold text-text-primary">Fees</TableHead>
              <TableHead className="font-heading font-bold text-text-primary">Status</TableHead>
              <TableHead className="font-heading font-bold text-text-primary text-right no-print">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-text-secondary">
                  No entries for this play day
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry) => (
                <TableRow 
                  key={entry.id} 
                  className={`hover:bg-surface-muted ${entry.is_paid ? 'bg-status-success/5' : ''}`}
                  data-testid={`entry-row-${entry.id}`}
                >
                  <TableCell className="font-medium text-text-primary">
                    {entry.contestant_name}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {entry.event_ids.slice(0, 3).map((eid) => (
                        <span key={eid} className="px-2 py-0.5 bg-primary/10 text-text-primary rounded text-xs">
                          {getEventName(eid)}
                        </span>
                      ))}
                      {entry.event_ids.length > 3 && (
                        <span className="px-2 py-0.5 bg-gray-100 text-text-muted rounded text-xs">
                          +{entry.event_ids.length - 3} more
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono font-bold text-text-primary">
                    ${entry.total_fees.toFixed(2)}
                  </TableCell>
                  <TableCell>
                    {entry.is_paid ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-status-success/20 text-status-success rounded text-xs font-medium">
                        <Check className="h-3 w-3" />
                        Paid ({entry.payment_type})
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-status-warning/20 text-status-warning rounded text-xs font-medium">
                        <X className="h-3 w-3" />
                        Unpaid
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right no-print">
                    {entry.is_paid ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleUncheckIn(entry.id)}
                        className="text-text-muted hover:text-status-error"
                        data-testid={`undo-checkin-${entry.id}`}
                      >
                        Undo
                      </Button>
                    ) : (
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCheckIn(entry.id, "cash")}
                          className="h-8"
                          data-testid={`checkin-cash-${entry.id}`}
                        >
                          <Banknote className="h-4 w-4 mr-1" />
                          Cash
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCheckIn(entry.id, "card")}
                          className="h-8"
                          data-testid={`checkin-card-${entry.id}`}
                        >
                          <CreditCard className="h-4 w-4 mr-1" />
                          Card
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCheckIn(entry.id, "check")}
                          className="h-8"
                          data-testid={`checkin-check-${entry.id}`}
                        >
                          Check
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default CheckInPage;
